import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { DialogShell } from './ui/DialogShell';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';
import { solicitudRolService, SolicitudRolWithDetails } from '../services/solicitudRolService';
import { usuarioService } from '../services/usuarioService';
import { formatDate, generateSecurePassword } from '../utils/formatters';
import { CredencialesGeneradasModal } from './CredencialesGeneradasModal';
import { supabase } from '../lib/supabase';
import { TextInput } from './ui/TextInput';
import { formatJerarquiaMiembro, difiereJerarquia } from '../utils/miembroJerarquia';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';

interface ResolverSolicitudRolModalProps {
  readonly solicitud: SolicitudRolWithDetails;
  /** Falso cuando el actor puede denegar la solicitud pero no asignar ese rol. */
  readonly puedeAprobar: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

// deuda: el componente concentra el detalle de la solicitud, la elección de acción, el formulario de
// comentario y la creación de la cuenta — complejidad ciclomática 35 y cognitiva 30 (React Doctor).
// techo: aguanta mientras resolver siga siendo aprobar o rechazar con un comentario; cada forma nueva
// de resolución lo empeora. disparador: al agregar un tercer tipo de resolución, o al volver a tocar
// el flujo de creación de cuenta, extraer el detalle y el formulario a componentes propios.
export const ResolverSolicitudRolModal: React.FC<ResolverSolicitudRolModalProps> = ({
  onClose,
  onSuccess,
  puedeAprobar,
  solicitud,
}) => {
  const toast = useToast();
  const { error, clearError, handleError } = useErrorHandler();
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<'aprobada' | 'rechazada' | null>(null);
  const [comment, setComment] = useState('');
  const [rolAplicado, setRolAplicado] = useState(false);
  const resolviendoRef = React.useRef(false);
  const [showCredencialesModal, setShowCredencialesModal] = useState(false);
  const [credencialesGeneradas, setCredencialesGeneradas] = useState<{
    email: string;
    password: string;
    nombreCompleto: string;
  } | null>(null);

  const esEliminacion = solicitud.tipo_solicitud === 'eliminacion';

  const aprobarEliminacion = async () => {
    await solicitudRolService.aprobar(solicitud.id, comment);
    toast.success(
      `Solicitud aprobada. ${solicitud.miembro.nombres} ${solicitud.miembro.apellidos} ya no tiene el rol ${solicitud.rol_a_eliminar?.nombre ?? ''}.`
    );
    setComment('');
    setAction(null);
    onSuccess();
    onClose();
  };

  const aprobarModificacion = async () => {
    await solicitudRolService.aprobar(solicitud.id, comment);
    setRolAplicado(true);
    try {
      const tieneUsuario = await verificarSiMiembroTieneUsuario(solicitud.miembro_id);
      if (tieneUsuario) {
        toast.success(
          `Solicitud aprobada exitosamente. ${solicitud.miembro.nombres} ${solicitud.miembro.apellidos} ahora tiene el rol ${solicitud.rol_solicitado?.nombre ?? ''}.`
        );
        onSuccess();
        onClose();
        return;
      }
      const password = generateSecurePassword(12);
      await usuarioService.updatePassword(solicitud.miembro_id, password);
      setCredencialesGeneradas({
        email: solicitud.miembro.email,
        password,
        nombreCompleto: `${solicitud.miembro.nombres} ${solicitud.miembro.apellidos}`,
      });
      setShowCredencialesModal(true);
      toast.success(
        `Solicitud aprobada y cuenta creada exitosamente para ${solicitud.miembro.nombres} ${solicitud.miembro.apellidos}.`
      );
    } catch (error: unknown) {
      handleError("El rol quedó asignado, pero no se pudo crear la cuenta. Usa 'Asignar contraseña' en Gestión de Usuarios.");
      if (!mensajeErrorDeNegocio(error)) {
        reportarErrorInesperado(error, 'No pudimos completar la creación de la cuenta.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resolviendoRef.current || rolAplicado) return;
    if (!solicitud || !action) return;

    handleError('');
    resolviendoRef.current = true;
    setLoading(true);

    try {
      if (action === 'aprobada') {
        if (esEliminacion) {
          await aprobarEliminacion();
        } else {
          await aprobarModificacion();
        }
      } else {
        await solicitudRolService.rechazar(solicitud.id, comment);
        toast.info('Solicitud rechazada. El miembro será notificado.');

        setComment('');
        setAction(null);
        onSuccess();
        onClose();
      }
    } catch (error: unknown) {
      const mensaje = mensajeErrorDeNegocio(error);
      if (mensaje) handleError(mensaje);
      else reportarErrorInesperado(error, 'No pudimos procesar la solicitud.');
    } finally {
      resolviendoRef.current = false;
      setLoading(false);
    }
  };

  const verificarSiMiembroTieneUsuario = async (miembroId: string): Promise<boolean> => {
    const { data, error } = await supabase.from('miembros')
      .select('user_id').eq('id', miembroId).single();
    if (error) throw error;
    if (!data) throw new Error('No se encontró el miembro');
    return Boolean(data.user_id);
  };

  const handleCloseCredencialesModal = () => {
    setShowCredencialesModal(false);
    setCredencialesGeneradas(null);
    setComment('');
    setAction(null);
    onSuccess();
    onClose();
  };

  const handleClose = () => {
    if (resolviendoRef.current) return;
    if (rolAplicado) onSuccess();
    setComment('');
    setAction(null);
    onClose();
  };

  let submitButtonLabel = 'Procesando...';
  if (!loading) {
    submitButtonLabel = action === 'aprobada' ? 'Confirmar Aprobación' : 'Confirmar Rechazo';
  }

  const hayDiscrepanciaJerarquia = difiereJerarquia(
    {
      provincia_eclesiastica_id: solicitud.provincia_eclesiastica?.id ?? null,
      diocesis_id: solicitud.diocesis?.id ?? null,
      lumisial_id: solicitud.lumisial?.id ?? null,
    },
    {
      provincia_eclesiastica_id: solicitud.miembro.lumisial?.provincia_eclesiastica?.id ?? null,
      diocesis_id: solicitud.miembro.lumisial?.diocesis?.id ?? null,
      lumisial_id: solicitud.miembro.lumisial?.id ?? null,
    }
  );

  return (
    <>
      {showCredencialesModal && credencialesGeneradas && (
        <CredencialesGeneradasModal
          isOpen={showCredencialesModal}
          onClose={handleCloseCredencialesModal}
          email={credencialesGeneradas.email}
          password={credencialesGeneradas.password}
          nombreCompleto={credencialesGeneradas.nombreCompleto}
        />
      )}

      <DialogShell
        isOpen={!showCredencialesModal}
        onClose={handleClose}
        title="Resolver Solicitud de Rol"
      >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormErrorAlert error={error} onClear={clearError} />

        {rolAplicado && (
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Cerrar
            </Button>
          </div>
        )}

        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Miembro</p>
            <p className="text-sm font-medium text-slate-900">
              {solicitud.miembro.nombres} {solicitud.miembro.apellidos}
            </p>
            <p className="text-xs text-slate-600">{solicitud.miembro.email}</p>
            {solicitud.miembro.lumisial && (
              <p className="text-xs text-slate-400 mt-1">
                {formatJerarquiaMiembro(solicitud.miembro.lumisial)}
              </p>
            )}
          </div>

          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Solicitante</p>
            <p className="text-sm font-medium text-slate-900">
              {solicitud.solicitante_miembro.nombres} {solicitud.solicitante_miembro.apellidos}
            </p>
            <p className="text-xs text-slate-600">{solicitud.solicitante_miembro.email}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">
              {esEliminacion ? 'Rol a Eliminar' : 'Rol Solicitado'}
            </p>
            <p className="text-sm font-medium text-slate-900">
              {esEliminacion ? solicitud.rol_a_eliminar?.nombre : solicitud.rol_solicitado?.nombre}
            </p>
            {!esEliminacion && solicitud.rol_solicitado?.descripcion && (
              <p className="text-xs text-slate-600">{solicitud.rol_solicitado.descripcion}</p>
            )}
          </div>

          {solicitud.lumisial && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase">Lumisial</p>
              <p className="text-sm text-slate-900">{solicitud.lumisial.nombre}</p>
            </div>
          )}

          {solicitud.diocesis && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase">Diócesis</p>
              <p className="text-sm text-slate-900">{solicitud.diocesis.nombre}</p>
            </div>
          )}

          {solicitud.provincia_eclesiastica && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase">Provincia Eclesiástica</p>
              <p className="text-sm text-slate-900">{solicitud.provincia_eclesiastica.nombre}</p>
            </div>
          )}

          {hayDiscrepanciaJerarquia && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                La jerarquía solicitada es distinta a la jerarquía real del miembro.
              </p>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Motivo de la Solicitud</p>
            <p className="text-sm text-slate-900 whitespace-pre-wrap">{solicitud.motivo}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Fecha de Solicitud</p>
            <p className="text-sm text-slate-900">{formatDate(solicitud.fecha_solicitud)}</p>
          </div>
        </div>

        {!action && !rolAplicado && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-700">
              ¿Qué acción desea tomar con esta solicitud?
            </p>
            <div className={`grid gap-3 ${puedeAprobar ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {puedeAprobar && (
                <Button
                  type="button"
                  onClick={() => setAction('aprobada')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Aprobar
                </Button>
              )}
              <Button
                type="button"
                onClick={() => setAction('rechazada')}
                className="bg-red-600 hover:bg-red-700"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Rechazar
              </Button>
            </div>
            {!puedeAprobar && (
              <p className="text-xs text-slate-500">
                Puedes denegar esta solicitud, pero asignar este rol corresponde a un cargo superior.
              </p>
            )}
          </div>
        )}

        {action && !rolAplicado && (
          <>
            <div className={`p-3 rounded-lg border ${
              action === 'aprobada'
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <p className="text-sm font-medium">
                {action === 'aprobada' ? (
                  <span className="text-green-800">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Aprobando solicitud
                  </span>
                ) : (
                  <span className="text-red-800">
                    <XCircle className="w-4 h-4 inline mr-1" />
                    Denegando solicitud
                  </span>
                )}
              </p>
            </div>

            <TextInput
              label={`Comentario ${action === 'aprobada' ? 'de Aprobación' : 'de Rechazo'}`}
              value={comment}
              onChange={(value) => setComment(value)}
              placeholder={
                action === 'aprobada'
                  ? 'Presidente JAL electo en asamblea general el 29/03/2025'
                  : 'Pendiente ratificación en reunión mensual'
              }
              required
            />

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={loading || !comment.trim()} className="flex-1">
                {submitButtonLabel}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setAction(null);
                  setComment('');
                }}
              >
                Cambiar
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                Cancelar
              </Button>
            </div>
          </>
        )}

        {!action && !rolAplicado && (
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
          </div>
        )}
      </form>
      </DialogShell>
    </>
  );
};
